import type { LanguageServicePosition } from './languageServiceProvider';
import type { OracleSchemaSnapshot } from './oracleSchemaTypes';
import { getOracleFunctionDoc, getOracleKeywordDoc } from './oracleLanguageMetadata';

type HoverResult = {
  contents: { kind: 'markdown'; value: string };
  range?: { start: LanguageServicePosition; end: LanguageServicePosition };
};

type IdentifierMatch = {
  text: string;
  startColumn: number;
  endColumnExclusive: number;
};

function getLineText(text: string, line: number): { lineText: string } {
  let currentLine = 0;
  let offset = 0;
  while (currentLine < line && offset < text.length) {
    const nl = text.indexOf('\n', offset);
    if (nl === -1) { offset = text.length; break; }
    offset = nl + 1;
    currentLine += 1;
  }
  const nextNl = text.indexOf('\n', offset);
  const end = nextNl === -1 ? text.length : nextNl;
  return { lineText: text.slice(offset, end) };
}

function findIdentifierAtColumn(lineText: string, column: number): IdentifierMatch | null {
  const regex = /"(?:[^"]|"")*"|[A-Za-z_][\w$#]*/g;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(lineText)) !== null) {
    const start = match.index;
    const end = start + match[0].length;
    if (column >= start && column <= end) {
      return { text: match[0], startColumn: start, endColumnExclusive: end };
    }
  }
  return null;
}

function unquoteIdentifier(value: string): string {
  const trimmed = value.trim();
  if (trimmed.startsWith('"') && trimmed.endsWith('"')) {
    return trimmed.slice(1, -1).replace(/""/g, '"');
  }
  return trimmed;
}

function formatObjectHover(snapshot: OracleSchemaSnapshot, objectName: string): HoverResult | null {
  const obj = snapshot.objectsByNameLower[objectName.toLowerCase()];
  if (!obj) return null;

  const typeLabel = obj.type === 'materialized_view' ? 'materialized view' : obj.type;
  const columnsPreview = obj.columns
    .slice(0, 10)
    .map((col) => {
      const tags = col.isPrimaryKey ? ' **PK**' : '';
      return `- \`${col.name}\` \`${col.dataType}\`${tags}`;
    })
    .join('\n');

  const more = obj.columns.length > 10 ? `\n- …and ${obj.columns.length - 10} more` : '';

  return {
    contents: {
      kind: 'markdown',
      value: `**${typeLabel}** \`${obj.schema}.${obj.name}\`\n\n${columnsPreview}${more}`,
    },
  };
}

function formatColumnHover(snapshot: OracleSchemaSnapshot, columnName: string): HoverResult | null {
  const matches = snapshot.columnIndexByNameLower[columnName.toLowerCase()] ?? [];
  if (matches.length === 0) return null;

  if (matches.length === 1) {
    const match = matches[0];
    const col = match.column;
    const nullable = col.isNullable ? 'YES' : 'NO';
    const pk = col.isPrimaryKey ? 'YES' : 'NO';
    const def = col.defaultValue != null ? `\n- Default: \`${col.defaultValue}\`` : '';
    return {
      contents: {
        kind: 'markdown',
        value: `**column** \`${col.name}\`\n\n- Object: \`${match.objectName}\` (${match.objectType})\n- Type: \`${col.dataType}\`\n- Nullable: ${nullable}\n- Primary key: ${pk}${def}`,
      },
    };
  }

  const list = matches
    .slice(0, 8)
    .map((match) => `- \`${match.objectName}\` (${match.objectType}) \`${match.column.dataType}\``)
    .join('\n');
  const more = matches.length > 8 ? `\n- …and ${matches.length - 8} more` : '';

  return {
    contents: {
      kind: 'markdown',
      value: `**column** \`${columnName}\`\n\n${list}${more}`,
    },
  };
}

function formatSequenceHover(snapshot: OracleSchemaSnapshot, name: string): HoverResult | null {
  const seq = snapshot.sequences.find((s) => s.name.toLowerCase() === name.toLowerCase());
  if (!seq) return null;
  return {
    contents: {
      kind: 'markdown',
      value: `**sequence** \`${seq.schema}.${seq.name}\`\n\nUse \`.NEXTVAL\` to get next value, \`.CURRVAL\` for current value.`,
    },
  };
}

function formatPackageHover(snapshot: OracleSchemaSnapshot, name: string): HoverResult | null {
  const pkg = snapshot.packages.find((p) => p.name.toLowerCase() === name.toLowerCase());
  if (!pkg) return null;

  const memberList = pkg.members.length > 0
    ? pkg.members
        .slice(0, 10)
        .map((m) => (m.kind === 'unknown' ? `- \`${m.name}\`` : `- \`${m.name}\` — ${m.kind}`))
        .join('\n')
    : '*(no members)*';
  const more = pkg.members.length > 10 ? `\n- …and ${pkg.members.length - 10} more` : '';

  return {
    contents: {
      kind: 'markdown',
      value: `**package** \`${pkg.schema}.${pkg.name}\`\n\n${memberList}${more}`,
    },
  };
}

export function getOracleHoverInfo(options: {
  text: string;
  position: LanguageServicePosition;
  snapshot?: OracleSchemaSnapshot;
}): HoverResult | null {
  const { lineText } = getLineText(options.text, options.position.line);
  const match = findIdentifierAtColumn(lineText, options.position.character);
  if (!match) return null;

  const raw = match.text;
  const identifier = unquoteIdentifier(raw);
  if (!identifier) return null;

  const range = {
    start: { line: options.position.line, character: match.startColumn },
    end: { line: options.position.line, character: match.endColumnExclusive },
  };

  const keywordDoc = getOracleKeywordDoc(identifier);
  if (keywordDoc) {
    return { contents: { kind: 'markdown', value: `**${identifier.toUpperCase()}**\n\n${keywordDoc}` }, range };
  }

  const fnDoc = getOracleFunctionDoc(identifier);
  if (fnDoc) {
    const sig = fnDoc.signature ? `\`${fnDoc.signature}\`` : `\`${fnDoc.name}\``;
    return { contents: { kind: 'markdown', value: `**function** ${sig}\n\n${fnDoc.description}` }, range };
  }

  if (options.snapshot) {
    const objectHover = formatObjectHover(options.snapshot, identifier);
    if (objectHover) return { ...objectHover, range };

    const sequenceHover = formatSequenceHover(options.snapshot, identifier);
    if (sequenceHover) return { ...sequenceHover, range };

    const packageHover = formatPackageHover(options.snapshot, identifier);
    if (packageHover) return { ...packageHover, range };

    const columnHover = formatColumnHover(options.snapshot, identifier);
    if (columnHover) return { ...columnHover, range };
  }

  return null;
}
